import { action, computed, makeObservable, observable } from "mobx";
import type {
  ExonnHubCheckboxFilterModel,
  ExonnHubFilterModel,
} from "src/admin/modules/b2b-gate-order/components/ExonnHub/ExonnHub.types.ts";
import { arrReplaceAt } from "@exonn/utils";
import type { ExonnHubProduct as BackendExonnHubProduct } from "@exonn/js-sdk";

export class ExonnHubStore {
  constructor({
    filtersModels = [],
    isFullscreen = false,
  }: {
    filtersModels?: ExonnHubFilterModel[];
    isFullscreen?: boolean;
  }) {
    this.filterModels = filtersModels;
    this.isFullscreen = isFullscreen;
    makeObservable(this);
  }
  @observable
  search: string = "";

  @action.bound
  setSearch(value: string) {
    this.search = value;
    this.setPage(1);
    this.setOpenedProduct(undefined);
  }

  @action
  clearSearch() {
    this.setSearch("");
    this.setDraftSearch("");
  }

  @observable
  draftSearch: string = "";

  @action.bound
  setDraftSearch(value: string) {
    this.draftSearch = value;
  }

  @observable
  categoryPath: string[] = [];

  @computed
  get currentCategory() {
    return this.categoryPath.length > 0
      ? this.categoryPath[this.categoryPath.length - 1]
      : undefined;
  }

  @action.bound
  setCategoryPath(value: string[]) {
    this.categoryPath = value;
  }

  @observable
  page: number = 1;

  @action.bound
  setPageSize(value: number) {
    this.pageSize = value;
  }

  @observable
  intensivelyPulsateWarning = false;

  @observable
  isWarningShaking: boolean = false;

  intensePulsationTimeout: NodeJS.Timeout | null = null;

  shakeWarningTimeout: NodeJS.Timeout | null = null;

  @observable
  pageSize: number = 10;

  @action.bound
  setPage(value: number) {
    this.page = value;
    this.closeProductPanel();
  }

  @observable
  sort: string = "default";

  @action.bound
  setSort(value: string | undefined) {
    if (value == null) {
      this.sort = "default";
    } else {
      this.sort = value;
    }
  }

  @observable
  isOrderPreviewOpen: boolean = false;

  @action.bound
  setIsOrderPreviewOpen(value: boolean) {
    this.isOrderPreviewOpen = value;
  }

  @observable
  isFullscreen: boolean = false;

  @action.bound
  setIsFullscreen(value: boolean) {
    this.isFullscreen = value;
  }

  @action.bound
  toggleFullscreen() {
    this.isFullscreen = !this.isFullscreen;
  }

  @observable
  showFavorite: boolean = false;

  @action.bound
  setShowFavorite(value: boolean) {
    this.showFavorite = value;
    this.setPage(1);
    this.setOpenedProduct(undefined);
  }

  @action.bound
  drawAttentionToWarning({
    intensifyPulsation,
    shake,
  }: {
    intensifyPulsation?: number;
    shake?: number;
  }) {
    this.clearShakingTimer();
    this.clearIntensePulsationTimer();

    if (intensifyPulsation) {
      this.intensivelyPulsateWarning = true;
      this.intensePulsationTimeout = setTimeout(() => {
        this.stopIntensePulsation();
      }, intensifyPulsation);
    }

    if (shake) {
      this.isWarningShaking = true;
      this.shakeWarningTimeout = setTimeout(() => {
        this.stopShaking();
      }, shake);
    }
  }

  @action.bound
  stopIntensePulsation() {
    this.intensivelyPulsateWarning = false;
    this.clearIntensePulsationTimer();
  }

  @action.bound
  stopShaking() {
    this.isWarningShaking = false;
    this.clearShakingTimer();
  }

  clearIntensePulsationTimer() {
    if (this.intensePulsationTimeout) {
      clearTimeout(this.intensePulsationTimeout);
    }
  }
  clearShakingTimer() {
    if (this.shakeWarningTimeout) {
      clearTimeout(this.shakeWarningTimeout);
    }
  }

  @action.bound
  toggleShowFavorite() {
    this.setShowFavorite(!this.showFavorite);
  }

  @observable
  isFilterPanelOpen: boolean = false;

  @action.bound
  setIsFilterPanelOpen(value: boolean) {
    this.isFilterPanelOpen = value;
  }

  @action.bound
  closeFilterPanel() {
    this.isFilterPanelOpen = false;
  }

  @action.bound
  openFilterPanel() {
    this.isFilterPanelOpen = true;
  }

  @action.bound
  toggleFilterPanel() {
    this.isFilterPanelOpen = !this.isFilterPanelOpen;
  }

  @observable
  openedProduct: BackendExonnHubProduct | undefined;

  @action.bound
  setOpenedProduct(value: BackendExonnHubProduct | undefined) {
    this.openedProduct = value;
  }

  @action.bound
  toggleProduct(product: BackendExonnHubProduct) {
    if (this.openedProduct?.id === product.id) {
      this.setOpenedProduct(undefined);
    } else {
      this.setOpenedProduct(product);
    }
  }

  @action.bound
  closeProductPanel() {
    this.openedProduct = undefined;
  }

  @computed
  get isProductPanelOpen() {
    return !!this.openedProduct;
  }

  @observable
  filterModels: ExonnHubFilterModel[] = [];

  @action.bound
  setFilterModels(value: ExonnHubFilterModel[]) {
    this.filterModels = value;
    this.setPage(1);
    this.setOpenedProduct(undefined);
  }

  @action.bound
  removeCheckboxFilterValue(
    filter: ExonnHubCheckboxFilterModel,
    value: string,
  ) {
    const updatedValues = filter.values.filter((value_) => value_ !== value);
    if (updatedValues.length > 0) {
      const index = this.filterModels.findIndex(
        (filter_) => (filter_.name = filter.name),
      );
      this.setFilterModels(
        arrReplaceAt(this.filterModels, index, {
          ...filter,
          values: updatedValues,
        }),
      );
    } else {
      this.removeFilter(filter.name);
    }
  }

  @action.bound
  removeFilter(name: string) {
    this.setFilterModels(
      this.filterModels.filter((filter) => filter.name !== name),
    );
  }

  @observable
  collapsedFilters: string[] = [];

  @action.bound
  setCollapsedFilters(value: string[]) {
    this.collapsedFilters = value;
  }

  @observable
  activeCategory: string | undefined = undefined;

  @action.bound
  setActiveCategory(value: string | undefined) {
    this.activeCategory = value;
    this.setPage(1);
    this.setOpenedProduct(undefined);
  }

  @observable
  hasScrolledItems: boolean = false;

  @action.bound
  setHasScrolledItems(value: boolean) {
    this.hasScrolledItems = value;
  }

  @observable
  categoriesExpanded: boolean = true;

  @action.bound
  setCategoriesExpanded(value: boolean) {
    this.categoriesExpanded = value;
  }
}
